/**
 * 월 정산 엑셀 내보내기 — 목록 선택 규칙 · 모달 머리 · 요청 본문 · 오류 문구 (순수 함수).
 *
 * 계획: ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 4-1(선택 열·선택 막대)·4-2(내보내기 모달)·6절(시험).
 * 서버 계약: ~/md/back/콜봇-월정산-정산시작일-엑셀내보내기-call-api요청.md 요청 계약 9절(POST /api/v1/callbot/usage/monthly/export).
 *
 *  - 선택은 부모(SettlementList)가 `Set<company_id>` 로 가진다. 여기 함수는 늘 **새 Set** 을 돌려준다(Vue 반응성 — 부모가 ref 에
 *    다시 넣는다). id 는 늘 String(company_id).
 *  - 전체 선택은 보이는 행만 넣고 뺀다. 칩·검색으로 가려진 선택은 그대로 남고 "가려진 n곳 포함"으로 알린다.
 *  - 한 번에 100곳까지(EXPORT_LIMIT). 넘으면 선택은 두고 내보내기만 막는다.
 *  - 달을 바꾸면 선택을 비운다(selectionAfterMonthChange).
 *  - 본문은 buildSettlementExportBody 만 만든다 — 키는 정확히 month·company_ids·contents·reasonCode(·reasonNote).
 *    auditLog.buildExportBody 는 쓰지 않는다(이용 내역 검색 본문용이라 from·to·companyId 를 붙여 call-api 전용 Pipe 가 400 으로
 *    거절하고, from·to 가 없으면 RangeError 를 던진다 — 계획서 4-2).
 *
 * vue·antd·xlsx 를 import 하지 않는다 — node --test 로 검증한다(service/__tests__/settlementExportSelection.test.mjs).
 */

import {
  getCallApiErrorBody,
  getCallApiErrorCode,
  getCallApiErrorStatus,
  isCallApiUnimplemented,
} from '../../service/callApiError.js';
import { supportEndedText } from '../../../../utils/standardErrors.js';

/** 한 번에 내보낼 수 있는 병원 수(call-api 본문 검증과 같다 — 4단계에서 회신 측정값으로 다시 정한다) */
export const EXPORT_LIMIT = 100;
export const EMPTY_SELECTION_TIP = '내보낼 병원을 먼저 고르세요';
export const MONTH_CHANGED_TEXT = '달을 바꿔 선택을 비웠습니다';
export const EXPORT_UNIMPLEMENTED_TEXT = '이 환경의 call-api 에 정산 내보내기 API 가 아직 없습니다(배포 전).';
/** 모달 '담을 내용' 체크박스 — confidential 이면 옆에 '대외비' */
export const EXPORT_CONTENT_OPTIONS = Object.freeze([
  Object.freeze({ key: 'statement', label: '정산서', sub: '공급가액·부가세·합계와 항목별 금액', confidential: false }),
  Object.freeze({ key: 'cost_margin', label: '원가·마진', sub: 'vox 원가, 시작 전 원가, 마진', confidential: true }),
]);
export const DEFAULT_EXPORT_CONTENTS = Object.freeze({ statement: true, cost_margin: false });
/** 모달 안내 — 확정 전 병원 */
export const UNCONFIRMED_NOTE = '확정 전인 병원은 저장된 값(검토 중·추정)으로 담기고 파일에 그렇게 표시됩니다.';
export const EMPTY_CONTENTS_TEXT = '담을 내용을 하나 이상 고르세요';

const MODAL_NAMES_SHOWN = 3;

const idOf = (v) => (v === null || v === undefined ? '' : String(v));
const arr = (v) => (Array.isArray(v) ? v : []);
const hasKorean = (s) => typeof s === 'string' && /[가-힣]/.test(s);

/** Set · 배열 · null → Set<string> (원본은 바꾸지 않는다) */
function asSet(selected) {
  if (selected instanceof Set) return new Set([...selected].map(idOf));
  if (Array.isArray(selected)) return new Set(selected.map(idOf));
  return new Set();
}

/** 보이는 id(중복·빈 값 없이, 순서 그대로) */
function visibleList(visibleIds) {
  const seen = new Set();
  const out = [];
  for (const raw of arr(visibleIds)) {
    const id = idOf(raw);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// 선택
// ---------------------------------------------------------------------------

/**
 * 행 하나 넣고/빼기
 * @param {Set<string>|string[]|null} selected
 * @param {string} id company_id
 * @param {boolean} checked
 * @returns {Set<string>} 새 Set
 */
export function toggleSelected(selected, id, checked) {
  const next = asSet(selected);
  const key = idOf(id);
  if (!key) return next;
  if (checked) next.add(key);
  else next.delete(key);
  return next;
}

/**
 * 머리글 전체 선택 — 보이는 행만 넣고/뺀다. 가려진 선택은 그대로 둔다.
 * @param {Set<string>|string[]|null} selected
 * @param {string[]} visibleIds 지금 표에 보이는 행의 company_id
 * @param {boolean} checked
 * @returns {Set<string>} 새 Set
 */
export function setVisibleSelected(selected, visibleIds, checked) {
  const next = asSet(selected);
  for (const id of visibleList(visibleIds)) {
    if (checked) next.add(id);
    else next.delete(id);
  }
  return next;
}

/**
 * 머리글 체크박스 상태 — 보이는 행이 모두 골라졌으면 checked, 일부면 indeterminate. 보이는 행이 없으면 둘 다 false.
 * @param {Set<string>|string[]|null} selected
 * @param {string[]} visibleIds
 * @returns {{ checked: boolean, indeterminate: boolean }}
 */
export function headerCheckState(selected, visibleIds) {
  const set = asSet(selected);
  const visible = visibleList(visibleIds);
  const picked = visible.filter((id) => set.has(id)).length;
  return {
    checked: visible.length > 0 && picked === visible.length,
    indeterminate: picked > 0 && picked < visible.length,
  };
}

/**
 * 다시 불러온 그 달 목록에 없는 id 를 뺀다
 * @param {Set<string>|string[]|null} selected
 * @param {Array<{ company_id: string }>} items 그 달 목록 전체(칩·검색과 상관없이)
 * @returns {Set<string>} 새 Set
 */
export function pruneSelection(selected, items) {
  const present = new Set(arr(items).map((it) => idOf(it?.company_id)));
  return new Set([...asSet(selected)].filter((id) => present.has(id)));
}

/**
 * 달을 바꿨을 때 — 선택을 비우고, 비었던 게 아니면 aria-live 로 알릴 문구
 * @param {Set<string>|string[]|null} selected
 * @returns {{ selected: Set<string>, notice: string }}
 */
export function selectionAfterMonthChange(selected) {
  return { selected: new Set(), notice: asSet(selected).size ? MONTH_CHANGED_TEXT : '' };
}

/**
 * 선택 막대 문구·버튼 상태
 * @param {Set<string>|string[]|null} selected
 * @param {string[]} visibleIds
 * @returns {{ count: number, hidden: number, overLimit: boolean, canExport: boolean, countText: string,
 *   hiddenText: string, limitText: string }}
 */
export function selectionSummary(selected, visibleIds) {
  const set = asSet(selected);
  const visible = new Set(visibleList(visibleIds));
  const count = set.size;
  const hidden = [...set].filter((id) => !visible.has(id)).length;
  const overLimit = count > EXPORT_LIMIT;
  return {
    count,
    hidden,
    overLimit,
    canExport: count >= 1 && !overLimit,
    countText: count ? `${count}곳 선택` : '',
    hiddenText: hidden ? `가려진 ${hidden}곳 포함` : '',
    limitText: overLimit ? `한 번에 ${EXPORT_LIMIT}곳까지 내보낼 수 있습니다(지금 ${count}곳)` : '',
  };
}

/**
 * 모달에 넘길 회사 — 목록 순서 그대로(같은 id 는 한 번만). 새 칸이 없는 목록이면 범위 'in'·시작일 null.
 * @param {Set<string>|string[]|null} selected
 * @param {Array<object>} items 그 달 목록 전체
 * @returns {Array<{ company_id: string, company_name: string, settlement_scope: string, settlement_start_date: string|null }>}
 */
export function exportCompaniesOf(selected, items) {
  const set = asSet(selected);
  const seen = new Set();
  const out = [];
  for (const it of arr(items)) {
    const id = idOf(it?.company_id);
    if (!id || !set.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push({
      company_id: id,
      company_name: typeof it.company_name === 'string' ? it.company_name : '',
      settlement_scope: typeof it.settlement_scope === 'string' ? it.settlement_scope : 'in',
      settlement_start_date: typeof it.settlement_start_date === 'string' ? it.settlement_start_date : null,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// 모달
// ---------------------------------------------------------------------------

const nameOf = (c) => (typeof c?.company_name === 'string' && c.company_name.trim() ? c.company_name : idOf(c?.company_id));

/**
 * 모달 머리 — 제목, 처음 3곳 + 외 n곳, 범위별 수(목록 항목의 settlement_scope 로 센다), 안내
 * @param {string} month 'YYYY-MM'
 * @param {Array<object>} companies exportCompaniesOf 결과(또는 상세의 회사 1곳)
 * @returns {{ title: string, names: string[], moreCount: number, allNames: string[], notBillableCount: number,
 *   startMonthCount: number, notes: string[] }}
 */
export function exportModalSummary(month, companies) {
  const list = arr(companies);
  const allNames = list.map(nameOf);
  const notBillableCount = list.filter((c) => c?.settlement_scope === 'before_start').length;
  const startMonthCount = list.filter((c) => c?.settlement_scope === 'start_month').length;
  const notes = [];
  if (notBillableCount) notes.push(`정산 대상 아님 ${notBillableCount}곳은 사용량만 담깁니다.`);
  notes.push(UNCONFIRMED_NOTE);
  return {
    title: `${month} · 병원 ${list.length}곳`,
    names: allNames.slice(0, MODAL_NAMES_SHOWN),
    moreCount: Math.max(0, allNames.length - MODAL_NAMES_SHOWN),
    allNames,
    notBillableCount,
    startMonthCount,
    notes,
  };
}

/**
 * '담을 내용' 검증 — 둘 다 끄면 문구, 아니면 ''
 * @param {{ statement?: boolean, cost_margin?: boolean }|null} contents
 * @returns {string}
 */
export function exportContentsError(contents) {
  return contents?.statement === true || contents?.cost_margin === true ? '' : EMPTY_CONTENTS_TEXT;
}

/**
 * 요청 본문(요청 계약 9절) — 키는 정확히 month · company_ids · contents · reasonCode (· reasonNote).
 *  - company_ids: String, 순서 그대로, 같은 id 는 한 번(서버는 중복을 400 으로 거절한다)
 *  - contents: 두 칸 모두 boolean(true 만 true)
 *  - reasonCode: 빈 값이면 키를 넣지 않는다(서버는 enforce 전까지 사유 없이도 받는다. 화면은 모달 검증으로 늘 묻는다)
 *  - reasonNote: trim 뒤 비면 키를 넣지 않는다
 * @param {{ month: string, companyIds: Iterable<string>, contents: object, reasonCode?: string, reasonNote?: string }} args
 * @returns {{ month: string, company_ids: string[], contents: { statement: boolean, cost_margin: boolean },
 *   reasonCode?: string, reasonNote?: string }}
 */
export function buildSettlementExportBody({ month, companyIds, contents, reasonCode, reasonNote } = {}) {
  const ids = [];
  const seen = new Set();
  for (const raw of companyIds ? [...companyIds] : []) {
    const id = idOf(raw);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  const body = {
    month,
    company_ids: ids,
    contents: { statement: contents?.statement === true, cost_margin: contents?.cost_margin === true },
  };
  if (typeof reasonCode === 'string' && reasonCode) body.reasonCode = reasonCode;
  const note = typeof reasonNote === 'string' ? reasonNote.trim() : '';
  if (note) body.reasonNote = note;
  return body;
}

// ---------------------------------------------------------------------------
// 결과 문구
// ---------------------------------------------------------------------------

/** 표준 계약 본문({ code, reason })과 call-api 봉투({ error: { code, reason } }) 둘 다 읽는다 */
function codeOf(err) {
  return getCallApiErrorCode(err) ?? (typeof err?.response?.data?.code === 'string' ? err.response.data.code : null);
}

function reasonOf(err) {
  return getCallApiErrorBody(err)?.error?.reason ?? err?.response?.data?.reason;
}

const EXPORT_ERROR_TEXT = {
  audit: '이용 내역을 남길 수 없어 내보내지 않았습니다. 잠시 후 다시 시도하세요.',
  perm: '권한을 확인하지 못해 내보내지 않았습니다. 잠시 뒤 다시 시도하세요.',
  denied: '정산 내보내기 권한이 없습니다. 관리자에게 권한을 요청하세요.',
  failed: '일부 병원의 정산 계산에 실패해 내보내지 않았습니다. 해당 병원 상세에서 다시 계산한 뒤 시도하세요.',
  timeout: '응답이 늦어 파일을 만들지 못했습니다. 이용 내역에는 남았을 수 있습니다. 고르는 병원 수를 줄여 다시 시도하세요.',
  validation: '내보내기 조건을 확인하세요.',
  other: '정산을 내보내지 못했습니다. 잠시 후 다시 시도하세요.',
};

/**
 * 내보내기 오류 → 모달 안 오류 문구(해결 방법 중심, 계획서 4-2). 503 이 두 가지라 상태가 아니라 error.code 로 나눈다.
 * @param {unknown} err axios 모양 오류(목 엔진도 같은 모양)
 * @returns {{ code: string|null, kind: 'audit'|'perm'|'support'|'denied'|'unknown_companies'|'failed'|'timeout'|
 *   'unimplemented'|'validation'|'other', text: string, reloadList: boolean }}
 */
export function exportErrorView(err) {
  const status = getCallApiErrorStatus(err);
  const code = codeOf(err) ?? null;
  const view = (kind, text, reloadList = false) => ({ code, kind, text, reloadList });

  // 응답 없이 시간 초과 — 서버는 이용 내역을 남겼을 수 있다
  if (status === undefined && err?.code === 'ECONNABORTED') return view('timeout', EXPORT_ERROR_TEXT.timeout);

  switch (code) {
    case 'AUDIT_UNAVAILABLE':
      return view('audit', EXPORT_ERROR_TEXT.audit);
    case 'PERM_UNAVAILABLE':
      return view('perm', EXPORT_ERROR_TEXT.perm);
    case 'SUPPORT_SESSION_ENDED':
      return view('support', `${supportEndedText(reasonOf(err))} 내보내지 않았습니다.`);
    case 'PERMISSION_DENIED':
      return view('denied', EXPORT_ERROR_TEXT.denied);
    case 'SETTLEMENT_EXPORT_FAILED':
      return view('failed', EXPORT_ERROR_TEXT.failed);
    default:
      break;
  }

  if (status === 400) {
    const unknown = arr(getCallApiErrorBody(err)?.error?.details?.unknown_company_ids);
    if (unknown.length) {
      return view(
        'unknown_companies',
        `고른 병원 중 ${unknown.length}곳이 이 달 목록에 없습니다. 목록을 새로 고친 뒤 다시 고르세요.`,
        true
      );
    }
    const message = getCallApiErrorBody(err)?.error?.message;
    return view('validation', hasKorean(message) ? message : EXPORT_ERROR_TEXT.validation);
  }
  if (isCallApiUnimplemented(err)) return view('unimplemented', EXPORT_UNIMPLEMENTED_TEXT);
  return view('other', EXPORT_ERROR_TEXT.other);
}

/** 담은 내용 이름 — '정산서 · 원가·마진' */
export function exportContentsText(contents) {
  return EXPORT_CONTENT_OPTIONS.filter((o) => contents?.[o.key] === true)
    .map((o) => o.label)
    .join(' · ');
}

/**
 * 성공 알림 — '병원 12곳 2026-10 정산을 내보냈습니다(정산서 · 원가·마진).' / 1곳이면 '가나치과 2026-10 정산을 내보냈습니다(정산서).'
 * @param {{ month: string, companies: Array<object>, contents: object }} args
 * @returns {string}
 */
export function exportSuccessText({ month, companies, contents } = {}) {
  const list = arr(companies);
  const who = list.length === 1 ? nameOf(list[0]) : `병원 ${list.length}곳`;
  return `${who} ${month} 정산을 내보냈습니다(${exportContentsText(contents)}).`;
}
